<template>
    <layout title="Member Balances and Gin Payments">
        <settings-layout>
            <div class="px-8">
                <div v-if="saving" class="animate-pulse rounded-md bg-blue-50 p-4 mb-4">
                    <div class="flex items-center justify-center">
                        <p class="text-sm text-blue-700">
                            Uploading...
                        </p>
                    </div>
                </div>
                <div class="flex items-center">
                    <h3 class="mr-2 flex-none">Choose Member:</h3>
                    <div class="flex-grow">
                        <select v-model="selectedUser" id="member" name="member" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                            <option v-for="member in users" v-bind:value="member">{{ member.name }}</option>
                        </select>
                    </div>
                </div>
            </div>
            <div v-if="selectedUser.name" class="px-8 mt-8 pt-8">
                <form @submit.prevent="submitBalance">
                    <div class="flex justify-between items-center w-full mb-8 pb-8 border-b">
                        <div>
                            <label for="starting_balance_date" class="block text-sm font-medium text-gray-700">Beginning Balance Date</label>
                            <text-input v-model="selectedUser.beginning_balance.balance_date" type="date" name="starting_balance_date" id="starting_balance_date" class="mt-1" />
                        </div>
                        <div>
                            <label for="starting_balance" class="block text-sm font-medium text-gray-700">Beginning Balance</label>
                            <text-input v-model="selectedUser.beginning_balance.balance" type="number" step=".01" name="starting_balance" id="starting_balance" class="mt-1" />
                        </div>
                        <div class="flex justify-end items-center mt-4">
                            <loading-button :loading="saving"  type="submit" class="">
                                Save
                            </loading-button>
                        </div>
                    </div>
                </form>
                <div class="flex justify-between items-center">
                    <div class="font-semibold text-lg">
                        <span class="text-gray-500">Upload for account:</span>
                        {{ selectedUser.gin_info.lawson_number }}
                    </div>

                    <label class="cursor-pointer -ml-px relative flex items-center px-4 py-2 border text-sm font-medium rounded-md text-blue-50 bg-blue-500 hover:bg-dark-color focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                        <svg class="w-6 h-6 mr-1 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <span>Import File</span>
                        <input @change="onChange" ref="file" class="hidden" type="file" name="files" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel">
                    </label>
                </div>
                <div class="mt-8 overflow-x-scroll w-full">
                    <daily-balance-listings :beginning-balance="selectedUser.beginning_balance" :items="selectedUser.gin_info.balanceItems"></daily-balance-listings>
                </div>
            </div>
        </settings-layout>
    </layout>
</template>

<script>
import Layout from '@/Shared/Layout'
import SettingsLayout from "./SettingsLayout"
import RemoveMemberFile from '@/Components/MemberFiles/RemoveMemberFile'
import LoadingButton from "@/Shared/LoadingButton";
import TextInput from '@/Shared/TextInput'
import DailyBalanceListings from "@/Components/DailyBalances/DailyBalanceListings";

export default {
    components: {SettingsLayout, Layout, RemoveMemberFile, LoadingButton, TextInput, DailyBalanceListings},
    metaInfo() {
        return {
            title: `Settings`,
        }
    },
    props: {
        users: {
            type: Array,
            default: [],
        },
        success: {
            type: Object,
            default: null
        },
        selectedUserReturn: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            saving: false,
            success: false,
            selectedUser: { files: [] },
            showMenu: null,
            sortKey: 'updated_at',
            sortOrder: 'desc',
            fileList: [],
        }
    },
    mounted() {
        if (this.selectedUserReturn) {
            this.selectedUser = this.selectedUserReturn
        }
    },
    methods: {
        submitBalance() {
            this.$inertia.post(this.route('settings.beginning-balances.store'), this.selectedUser, {
                onStart: () => this.saving = true,
                onFinish: () => this.saving = false,
                onSuccess: () => this.saving = false,
            })
        },
        setShowMenu(id) {
            this.showMenu = this.showMenu === id ? null : id
        },
        sortBy(key) {
            this.sortKey = key
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
        },
        compareValues(key, order = 'asc') {
            return function innerSort(a, b) {
                if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;
                const comparison = a[key].toString().localeCompare(b[key].toString());

                return (
                    (order === 'desc') ? (comparison * -1) : comparison
                );
            };
        },
        onChange() {
            let data = new FormData()
            data.append('user_id', this.selectedUser.id)
            data.append('lawson_number', this.selectedUser.gin_info.lawson_number)
            this.fileList = [...this.$refs.file.files]
            this.fileList.forEach(file => data.append(`importFiles[]`, file))
            this.$inertia.post(this.route('settings.balances.store'), data,
                {
                    onStart: () => this.saving = true,
                    onFinish: () => this.saving = false,
                    onSuccess: () => this.saving = false
                }
            )
        }
    },
    computed: {
        sortedMemberFiles() {
            return this.selectedUser.files.sort(
                this.compareValues(this.sortKey, this.sortOrder)
            )
        }
    },
    watch: {
        selectedUser() {
            if (! this.selectedUser.beginning_balance) {
                this.selectedUser.beginning_balance = {
                    balance_date: Date.now(),
                    balance: 0
                }
            }
        },
        selectedUserReturn() {
            if (this.selectedUserReturn > 0) {
                this.selectedUser = this.users.find(user => user.id === parseInt(this.selectedUserReturn))
            }
        },
        users: {
            deep: true,
            handler() {
                if (this.selectedUserReturn > 0) {
                    this.selectedUser = this.users.find(user => user.id === parseInt(this.selectedUserReturn))
                }
            }
        }
    }
}
</script>
